import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';

/**
 * Enforces max quantity of 1 per item across the entire cart.
 * @param {{ skipUIRefresh?: boolean }} [options]
 */
async function enforceCartQuantityLimit({ skipUIRefresh = false } = {}) {
  const response = await fetch('/cart.js');
  const cart = await response.json();

  const overLimit = cart.items.filter((item) => item.quantity > 1);
  if (overLimit.length === 0) return;

  const updates = {};
  overLimit.forEach((item) => {
    updates[item.variant_id] = 1;
  });

  await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ updates }),
  });

  if (!skipUIRefresh) {
    document.dispatchEvent(new CartUpdateEvent({}, 'cart-quantity-limit', {}));
  }
}

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #isCheckingOut = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('submit', this.#handleCartFormSubmit, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('submit', this.#handleCartFormSubmit, true);
  }

  #handleCartAdd = async (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
    if (!event.detail?.data?.didError) {
      try {
        await enforceCartQuantityLimit();
      } catch (error) {
        console.error('cart-quantity-limit error:', error);
      }
    }
  };

  #handleCartFormSubmit = async (event) => {
    if (this.#isCheckingOut) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;
    if (event.submitter?.name !== 'checkout') return;

    event.preventDefault();
    this.#isCheckingOut = true;

    try {
      await enforceCartQuantityLimit({ skipUIRefresh: true });
    } catch (error) {
      console.error('cart-quantity-limit checkout error:', error);
    }

    form.requestSubmit(event.submitter);
    this.#isCheckingOut = false;
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
